import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  return (
    (<DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-6 sm:space-y-0",
        month: "space-y-3",
        caption: "flex justify-center pt-1.5 pb-1 relative items-center",
        caption_label: "text-[15px] font-medium text-white/90 tracking-tight",
        nav: "space-x-1 flex items-center",
        // Quiet glass chevrons that match the app's pill controls — the old
        // outline buttons at opacity-50 read as disabled.
        nav_button: cn(
          "inline-flex items-center justify-center h-8 w-8 rounded-full",
          "border border-white/10 bg-white/[0.04] text-white/60",
          "hover:bg-white/[0.09] hover:text-white hover:border-white/20 transition-colors"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          "text-white/35 rounded-md w-9 md:w-10 flex-1 md:flex-none font-normal text-[11px] uppercase tracking-[0.08em]",
        row: "flex w-full mt-1.5",
        cell: cn(
          "relative p-0 text-center text-sm flex-1 focus-within:relative focus-within:z-20",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-full [&:has(>.day-range-start)]:rounded-l-full first:[&:has([aria-selected])]:rounded-l-full last:[&:has([aria-selected])]:rounded-r-full"
            : "[&:has([aria-selected])]:rounded-full"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-full md:w-10 p-0 rounded-full font-normal text-white/85",
          "hover:bg-white/[0.09] hover:text-white aria-selected:opacity-100 transition-colors"
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        // Brand endpoints (CalendarRangeStyles carries the authoritative CSS;
        // these keep single-date mode consistent when the style tag is absent).
        day_selected:
          "bg-[hsl(228,62%,50%)] text-white hover:bg-[hsl(228,62%,56%)] hover:text-white focus:bg-[hsl(228,62%,50%)] focus:text-white shadow-[0_0_16px_-4px_hsla(228,80%,60%,0.55)]",
        day_today: "ring-1 ring-inset ring-white/25 text-white",
        day_outside:
          "day-outside text-white/20 aria-selected:bg-transparent aria-selected:text-white/25",
        // Unbookable days recede completely instead of shadowing real dates.
        day_disabled: "text-white/[0.16] hover:bg-transparent hover:text-white/[0.16]",
        day_range_middle:
          "aria-selected:bg-transparent aria-selected:text-white/90",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn("h-4 w-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn("h-4 w-4", className)} {...props} />
        ),
      }}
      {...props} />)
  );
}
Calendar.displayName = "Calendar"

export { Calendar }
